import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const nav = useNavigate();
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) nav("/", { replace: true });
  }, [user, nav]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const onGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Login berhasil");
      nav("/", { replace: true });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#f7f9f7]">
      {/* Left panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-green-50 bg-[#14532d] overflow-hidden">
        <div className="absolute inset-0 islamic-pattern opacity-40" />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 grid place-items-center rounded-lg bg-white/10 font-display text-2xl font-bold">IM</div>
            <div>
              <div className="font-display text-2xl font-bold tracking-tight">IMSYAF</div>
              <div className="text-xs uppercase tracking-widest text-green-200/80">Integrated Management System</div>
            </div>
          </div>
          <h1 className="mt-24 font-display text-5xl font-bold leading-tight tracking-tight">
            Kelola pesantren <br /> dengan tenang, <br /> aman, dan terintegrasi.
          </h1>
          <p className="mt-4 text-green-100/85 max-w-md text-[15px]">
            Satu portal untuk data santri, absensi, tahfiz, pelanggaran, laporan, dan lebih banyak lagi — 
            khusus Pondok Pesantren Al-Imam Asy-Syafi'i Tarakan.
          </p>
        </div>
        <div className="relative z-10 text-xs text-green-200/70">
          © {new Date().getFullYear()} PP Al-Imam Asy-Syafi'i Tarakan
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 lg:p-16">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.24em] text-[#166534]">Masuk</div>
            <h2 className="font-display text-3xl font-bold text-[#064E3B] mt-1">Selamat datang kembali</h2>
            <p className="text-sm text-slate-500 mt-2">Gunakan email dan password akun Anda.</p>
          </div>
          <form className="space-y-4" onSubmit={onLogin}>
            <div>
              <label className="text-xs font-medium text-slate-600">Email</label>
              <input
                type="email"
                className="imsyaf-input mt-1"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@pesantren.id"
                data-testid="login-email-input"
                required
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Password</label>
              <input
                type="password"
                className="imsyaf-input mt-1"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="imsyaf-btn-primary w-full justify-center disabled:opacity-60"
            >
              {loading ? "Memproses..." : "Masuk"}
            </button>
            <div className="text-right">
              <Link to="/forgot-password" className="text-xs text-[#166534] hover:underline" data-testid="login-forgot-link">
                Lupa password?
              </Link>
            </div>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px bg-slate-200 flex-1" />
            <span className="text-[11px] uppercase tracking-widest text-slate-400">atau</span>
            <div className="h-px bg-slate-200 flex-1" />
          </div>

          <button
            type="button"
            onClick={onGoogleLogin}
            data-testid="login-google-btn"
            className="w-full inline-flex items-center justify-center gap-3 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.56c2.08-1.92 3.28-4.74 3.28-8.1Z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.56-2.77c-.99.66-2.25 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/>
              <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.2 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/>
            </svg>
            Masuk dengan Google
          </button>
          <p className="mt-3 text-[11px] text-slate-400 text-center">
            Hanya email yang sudah didaftarkan admin yang bisa masuk via Google.
          </p>
        </div>
      </div>
    </div>
  );
}
